import React, { useState, useEffect, useCallback, useRef } from 'react';
import { WarnIcon } from '../components/icons';
import Layout from '../components/Layout';
import SchemaCatalog from '../components/reports/SchemaCatalog';
import FilterBuilder from '../components/reports/FilterBuilder';
import PivotConfig from '../components/reports/PivotConfig';
import CustomJoinPanel from '../components/reports/CustomJoinPanel';
import SavedReportsTree from '../components/reports/SavedReportsTree';
import ResultsTable from '../components/reports/ResultsTable';
import { reportsApi } from '../utils/api';

const PAGE_SIZE = 50;

export default function Reports() {
  // Schema
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [schemaError, setSchemaError] = useState(null);

  // Report config
  const [reportType, setReportType] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const [mode, setMode] = useState('tabular'); // tabular | pivot
  const [pivotRows, setPivotRows] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [customJoins, setCustomJoins] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [orderBy, setOrderBy] = useState(null);
  const [page, setPage] = useState(0);

  // Results
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Saved reports
  const [savedReports, setSavedReports] = useState([]);
  const [loadWarning, setLoadWarning] = useState(null);

  // Sidebar section
  const [sidebarTab, setSidebarTab] = useState('catalog');

  // Save form (inline in action bar)
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveFolder, setSaveFolder] = useState('');
  const [saveError, setSaveError] = useState(null);
  const [savingReport, setSavingReport] = useState(false);

  // Drag state for column reorder
  const dragIdx = useRef(null);

  // Fetch schema + saved reports
  useEffect(() => {
    (async () => {
      try {
        const [schemaRes, savedRes] = await Promise.all([
          reportsApi.schema(),
          reportsApi.listSaved(),
        ]);
        setSchema(schemaRes.data.reportTypes);
        setSavedReports(savedRes.data.savedReports);
      } catch (e) {
        console.error('Failed to load report data:', e);
        const msg = e.response?.status
          ? `API error ${e.response.status}: ${e.response.data?.error || e.message}`
          : `Network error: ${e.message}`;
        setSchemaError(msg);
      } finally {
        setSchemaLoading(false);
      }
    })();
  }, []);

  const currentSchema = schema?.find(s => s.key === reportType) || null;
  const allColumns = currentSchema ? [...currentSchema.columns] : [];

  // Column label lookup
  const colLabelMap = {};
  for (const c of allColumns) colLabelMap[c.key] = c;

  // Reset on report type change, optionally pre-selecting a column
  const handleSelectReportType = (key, initialColumn) => {
    if (key === reportType) return;
    setReportType(key);
    setSelectedColumns(initialColumn ? [initialColumn] : []);
    setFilters([]);
    setMode('tabular');
    setPivotRows([]);
    setMeasures([]);
    setCustomJoins([]);
    setShowAdvanced(false);
    setOrderBy(null);
    setResults(null);
    setError(null);
    setPage(0);
    setLoadWarning(null);
  };

  // Load saved report — with validation
  const handleLoadSaved = (saved) => {
    const cfg = saved.config;
    const targetType = cfg.reportType || null;
    const targetSchema = schema?.find(s => s.key === targetType);

    if (!targetSchema) {
      setLoadWarning(`Report type "${targetType}" no longer exists. This saved report cannot be loaded.`);
      return;
    }

    const validColKeys = new Set(targetSchema.columns.map(c => c.key));
    const warnings = [];

    // Validate columns
    const validCols = (cfg.columns || []).filter(k => {
      if (validColKeys.has(k)) return true;
      warnings.push(`Column "${k}" removed (no longer exists)`);
      return false;
    });

    // Validate filters
    const validFilters = (cfg.filters || []).filter(f => {
      if (!f.field || validColKeys.has(f.field)) return true;
      warnings.push(`Filter on "${f.field}" removed (column no longer exists)`);
      return false;
    });

    // Validate pivot rows
    const validPivotRows = (cfg.pivotRows || cfg.groupBy || []).filter(k => {
      if (validColKeys.has(k)) return true;
      warnings.push(`Pivot row "${k}" removed (column no longer exists)`);
      return false;
    });

    // Validate measures
    const validMeasures = (cfg.measures || []).filter(m => {
      if (!m.column || validColKeys.has(m.column)) return true;
      warnings.push(`Measure on "${m.column}" removed (column no longer exists)`);
      return false;
    });

    setReportType(targetType);
    setSelectedColumns(validCols);
    setFilters(validFilters);
    setMode(cfg.mode || 'tabular');
    setPivotRows(validPivotRows);
    setMeasures(validMeasures);
    setCustomJoins(cfg.customJoins || []);
    setShowAdvanced((cfg.customJoins || []).length > 0);
    setOrderBy(cfg.orderBy || null);
    setResults(null);
    setError(null);
    setPage(0);

    if (warnings.length > 0) {
      setLoadWarning(`Loaded "${saved.name}" with adjustments:\n${warnings.join('\n')}`);
    } else {
      setLoadWarning(null);
    }
  };

  // Save
  const handleSave = async (name, folder) => {
    const config = {
      reportType,
      columns: selectedColumns,
      filters,
      mode,
      pivotRows,
      groupBy: pivotRows,
      measures,
      customJoins,
      orderBy,
    };
    const res = await reportsApi.createSaved({ name, folder, config });
    setSavedReports(prev => [res.data, ...prev]);
  };

  // Delete saved
  const handleDeleteSaved = async (id) => {
    try {
      await reportsApi.deleteSaved(id);
      setSavedReports(prev => prev.filter(s => s.id !== id));
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  // Save from action bar
  const handleSaveFromBar = async () => {
    if (!saveName.trim()) return;
    setSavingReport(true);
    setSaveError(null);
    try {
      const config = {
        reportType,
        columns: selectedColumns,
        filters,
        mode,
        pivotRows,
        groupBy: pivotRows,
        measures,
        customJoins,
        orderBy,
      };
      const res = await reportsApi.createSaved({ name: saveName.trim(), folder: saveFolder.trim() || null, config });
      setSavedReports(prev => [res.data, ...prev]);
      setShowSaveForm(false);
      setSaveName('');
      setSaveFolder('');
    } catch (e) {
      setSaveError(e.response?.data?.error || 'Failed to save report');
    } finally {
      setSavingReport(false);
    }
  };

  // Existing folder names for save dropdown
  const existingFolders = [...new Set(savedReports.filter(r => r.folder).map(r => r.folder))].sort();

  // Column toggle (from sidebar click)
  const toggleColumn = (key) => {
    setSelectedColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Drag and drop reorder for selected columns
  const handleDragStart = (idx) => { dragIdx.current = idx; };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    const fromIdx = dragIdx.current;
    if (fromIdx === null || fromIdx === dropIdx) return;
    setSelectedColumns(prev => {
      const next = [...prev];
      const [item] = next.splice(fromIdx, 1);
      next.splice(dropIdx, 0, item);
      return next;
    });
    dragIdx.current = null;
  };

  // Run report
  const runReport = useCallback(async (pageOverride) => {
    if (!reportType) return;
    setLoading(true);
    setError(null);
    const currentPage = pageOverride !== undefined ? pageOverride : page;

    try {
      const isPivot = mode === 'pivot';
      const body = {
        reportType,
        columns: isPivot ? [] : selectedColumns,
        filters: filters.filter(f => f.field && f.operator),
        groupBy: isPivot ? pivotRows : [],
        measures: isPivot ? measures.filter(m => m.column && m.function) : [],
        customJoins: customJoins.filter(cj => cj.table && cj.conditions?.length > 0),
        orderBy: orderBy || undefined,
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE,
      };
      const res = await reportsApi.query(body);
      setResults(res.data);
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to run report';
      setError(msg);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [reportType, selectedColumns, filters, mode, pivotRows, measures, customJoins, orderBy, page]);

  // Export
  const handleExport = async (format) => {
    if (!reportType) return;
    setExporting(true);
    try {
      const isPivot = mode === 'pivot';
      const body = {
        reportType,
        columns: isPivot ? [] : selectedColumns,
        filters: filters.filter(f => f.field && f.operator),
        groupBy: isPivot ? pivotRows : [],
        measures: isPivot ? measures.filter(m => m.column && m.function) : [],
        customJoins: customJoins.filter(cj => cj.table && cj.conditions?.length > 0),
        orderBy: orderBy || undefined,
      };
      const res = await reportsApi.exportFile(body, format);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}-report-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExporting(false);
    }
  };

  // Sort
  const handleSort = (colKey) => {
    setOrderBy(prev => {
      if (prev?.column === colKey) {
        return { column: colKey, direction: prev.direction === 'ASC' ? 'DESC' : 'ASC' };
      }
      return { column: colKey, direction: 'ASC' };
    });
  };

  // Page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
    runReport(newPage);
  };

  if (schemaLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex -mx-4 sm:-mx-6 lg:-mx-8 -my-8 h-[calc(100vh-4rem)] lg:h-screen overflow-hidden">
        {/* ---- LEFT SIDEBAR ---- */}
        <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
          {/* Sidebar tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setSidebarTab('catalog')}
              className={`flex-1 px-3 py-2 text-xs font-medium ${
                sidebarTab === 'catalog'
                  ? 'text-primary-700 border-b-2 border-primary-600 bg-primary-50'
                  : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Data Sources
            </button>
            <button
              onClick={() => setSidebarTab('saved')}
              className={`flex-1 px-3 py-2 text-xs font-medium ${
                sidebarTab === 'saved'
                  ? 'text-primary-700 border-b-2 border-primary-600 bg-primary-50'
                  : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Saved
              {savedReports.length > 0 && (
                <span className="ml-1 text-[10px] text-gray-600">({savedReports.length})</span>
              )}
            </button>
          </div>

          {/* Sidebar content */}
          <div className="flex-1 overflow-y-auto">
            {sidebarTab === 'catalog' ? (
              schema ? (
                <SchemaCatalog
                  schema={schema}
                  selectedReportType={reportType}
                  onSelectReportType={handleSelectReportType}
                  selectedColumns={selectedColumns}
                  onToggleColumn={toggleColumn}
                  mode={mode}
                />
              ) : (
                <div className="px-3 py-4 text-xs text-gray-600">
                  {schemaLoading ? 'Loading...' : (schemaError || 'Failed to load data sources. Is the backend running?')}
                </div>
              )
            ) : (
              <SavedReportsTree
                savedReports={savedReports}
                onLoad={handleLoadSaved}
                onDelete={handleDeleteSaved}
                onSave={handleSave}
                canSave={!!reportType}
              />
            )}
          </div>
        </div>

        {/* ---- MAIN CONTENT ---- */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!reportType ? (
            /* Empty state */
            <div className="flex-1 flex flex-col items-center justify-center text-gray-600">
              <svg className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" strokeWidth={0.75} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125v-3.75m16.5 3.75v3.75c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125v-3.75" />
              </svg>
              <h3 className="text-lg font-medium text-gray-600 mb-1">Select a data source</h3>
              <p className="text-sm">Choose a table from the Data Sources panel to start building a report</p>
            </div>
          ) : (
            <>
              {/* Query builder header */}
              <div className="border-b border-gray-200 bg-white px-5 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">{currentSchema?.label}</h2>
                    <p className="text-xs text-gray-600">{currentSchema?.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Mode toggle */}
                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                      <button
                        onClick={() => setMode('tabular')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          mode === 'tabular'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-700'
                        }`}
                      >
                        Tabular
                      </button>
                      <button
                        onClick={() => setMode('pivot')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          mode === 'pivot'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-700'
                        }`}
                      >
                        Pivot
                      </button>
                    </div>

                    {/* Advanced toggle */}
                    <button
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                        showAdvanced
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      Advanced
                      {customJoins.length > 0 && (
                        <span className="ml-1 bg-amber-200 text-amber-800 rounded-full px-1.5 text-[10px]">
                          {customJoins.length}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Load warning */}
              {loadWarning && (
                <div className="mx-5 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5"><WarnIcon /></span>
                  <div className="flex-1 whitespace-pre-line">{loadWarning}</div>
                  <button onClick={() => setLoadWarning(null)} className="text-amber-400 hover:text-amber-600 flex-shrink-0">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Query builder body */}
              <div className="flex-1 overflow-y-auto">
                <div className="px-5 py-4 space-y-5">

                  {/* Tabular: Selected columns — drag to reorder */}
                  {mode === 'tabular' && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                        Selected Columns
                        <span className="ml-1 text-gray-600 normal-case font-normal">
                          {selectedColumns.length > 0
                            ? `(${selectedColumns.length}) \u2014 click columns in the sidebar to add, drag to reorder`
                            : '\u2014 click columns in the sidebar to add'
                          }
                        </span>
                      </h3>

                      {selectedColumns.length === 0 ? (
                        <div className="border-2 border-dashed border-gray-200 rounded-lg px-4 py-6 text-center">
                          <p className="text-xs text-gray-600">
                            No columns selected — defaults will be used.
                            <br />
                            Expand a data source in the sidebar and click columns to add them.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          {selectedColumns.map((key, idx) => {
                            const col = colLabelMap[key];
                            if (!col) return null;
                            return (
                              <div
                                key={key}
                                draggable
                                onDragStart={() => handleDragStart(idx)}
                                onDragOver={(e) => handleDragOver(e, idx)}
                                onDrop={(e) => handleDrop(e, idx)}
                                className="flex items-center gap-2 px-2.5 py-1.5 bg-white border border-gray-200 rounded-md hover:border-gray-300 cursor-grab active:cursor-grabbing group"
                              >
                                {/* Drag handle */}
                                <svg className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                  <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
                                  <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                                  <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
                                </svg>

                                {/* Position number */}
                                <span className="text-[10px] text-gray-600 w-4 text-center flex-shrink-0">{idx + 1}</span>

                                {/* Type icon */}
                                <TypeIcon type={col.type} />

                                {/* Label */}
                                <span className="text-xs text-gray-700 flex-1 truncate">{col.label}</span>

                                {/* Remove */}
                                <button
                                  onClick={() => toggleColumn(key)}
                                  className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 p-0.5 flex-shrink-0"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pivot config */}
                  {mode === 'pivot' && (
                    <PivotConfig
                      columns={allColumns}
                      pivotRows={pivotRows}
                      measures={measures}
                      onChangePivotRows={setPivotRows}
                      onChangeMeasures={setMeasures}
                    />
                  )}

                  {/* Filters */}
                  <FilterBuilder
                    columns={allColumns}
                    filters={filters}
                    onChange={setFilters}
                  />

                  {/* Advanced: Custom Joins */}
                  {showAdvanced && (
                    <CustomJoinPanel
                      reportType={reportType}
                      customJoins={customJoins}
                      onChangeCustomJoins={setCustomJoins}
                      schema={currentSchema}
                    />
                  )}

                  {/* Action bar */}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => { setPage(0); runReport(0); }}
                      disabled={loading}
                      className="bg-primary-600 text-white px-4 py-1.5 text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                    >
                      {loading ? (
                        <span className="inline-flex items-center gap-1.5">
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                          Running...
                        </span>
                      ) : (
                        'Run Report'
                      )}
                    </button>

                    <button
                      onClick={() => handleExport('csv')}
                      disabled={exporting || !results}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                    >
                      CSV
                    </button>
                    <button
                      onClick={() => handleExport('xlsx')}
                      disabled={exporting || !results}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                    >
                      Excel
                    </button>

                    <div className="border-l border-gray-200 h-5 mx-1"></div>

                    {/* Save report */}
                    <button
                      onClick={() => setShowSaveForm(!showSaveForm)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                      </svg>
                      Save Report
                    </button>

                    {results && (
                      <span className="text-xs text-gray-600 ml-auto">
                        {results.totalRows.toLocaleString()} total rows
                        {results.mode === 'summary' && ' (grouped)'}
                      </span>
                    )}
                  </div>

                  {/* Inline save form */}
                  {showSaveForm && (
                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                        </svg>
                        <span className="text-xs font-semibold text-gray-700">Save Report</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={saveName}
                          onChange={e => setSaveName(e.target.value)}
                          placeholder="Report name..."
                          className="flex-1 border border-gray-300 rounded-md text-xs px-2.5 py-1.5 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          onKeyDown={e => e.key === 'Enter' && handleSaveFromBar()}
                          autoFocus
                        />
                        <select
                          value={saveFolder}
                          onChange={e => setSaveFolder(e.target.value)}
                          className="border border-gray-300 rounded-md text-xs px-2 py-1.5 bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">No folder</option>
                          {existingFolders.map(f => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={saveFolder}
                          onChange={e => setSaveFolder(e.target.value)}
                          placeholder="or new folder..."
                          className="border border-gray-300 rounded-md text-xs px-2.5 py-1.5 w-32 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <button
                          onClick={handleSaveFromBar}
                          disabled={savingReport || !saveName.trim()}
                          className="bg-primary-600 text-white px-3 py-1.5 text-xs font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 transition-colors"
                        >
                          {savingReport ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => { setShowSaveForm(false); setSaveName(''); setSaveFolder(''); setSaveError(null); }}
                          className="text-gray-600 hover:text-gray-600 p-1"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      {saveError && (
                        <p className="mt-1.5 text-xs text-red-600">{saveError}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Results */}
                <div className="px-5 pb-5">
                  <ResultsTable
                    results={results}
                    loading={loading}
                    error={error}
                    orderBy={orderBy}
                    onSort={handleSort}
                    page={page}
                    pageSize={PAGE_SIZE}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

function TypeIcon({ type }) {
  const config = {
    string: { symbol: 'Aa', color: 'text-blue-400' },
    number: { symbol: '#', color: 'text-green-500' },
    date: { symbol: '\u29D6', color: 'text-amber-400' },
    enum: { symbol: '\u2261', color: 'text-purple-400' },
  };
  const c = config[type] || config.string;
  return (
    <span className={`font-mono text-[9px] ${c.color}`}>{c.symbol}</span>
  );
}
